import {
  Card,
  Flex,
  Graph,
  RenderControl,
  TGraphType,
  useWindowDimensions
} from '@fareye/ui'
import { cloneDeep, isEmpty, toTileCase } from '@fareye/utils'
import React from 'react'
import { GraphRendererContainer } from '../../style/styled'
import { generateDataForChart } from '../../utility'
import {
  IApiChartConfig,
  IChartConfig,
  IDimension,
  IDrillDownDimension,
  IFilterData,
  IGraphRendererConfig,
  IGraphStateData,
  IKeyValue,
  TGraphStateConfig,
  TGraphStateDataKeys
} from '../../utility/types'
import DimensionTraveller from './components/DimensionTraveller'
import GraphHeader from './components/Header'
import ScoreCard from './components/ScoreCard'
import { useTranslation } from '@fareye/common'

const GraphRenderer = (props: IGraphRendererConfig) => {
  let {
    dataConfig,
    graphConfig,
    style: graphStyle,
    globalFilter,
    FullScreenComponent
  } = props
  const { graphDataMapping } = dataConfig
  const { showLegends, pageSize, chartTitle, chartId } = graphConfig
  const [selectedDimension, setSelectedDimension] = React.useState(
    graphConfig.dimensions[0].dimension[graphDataMapping.dimension.value]
  )
  const [
    selectedDrillDownDimensionIndex,
    setSelectedDrillDownDimensionIndex
  ] = React.useState({ isReload: true, index: 0 })
  const [selectedGraphPoint, setSelectedGraphPoint] = React.useState({})
  const [graphStateData, setGraphStateData] = React.useState<
    IKeyValue<IGraphStateData[]>
  >({})
  const [loading, setLoading] = React.useState(false)
  const [isFirstTime, setIsFirstTime] = React.useState(true)
  const [showGraph, setShowGraph] = React.useState(true)
  const [benchmarkInput, setBenchMarkInput] = React.useState(1000)
  const events = {
    onClick: (value: any) => {
      if (
        graphStateData[selectedDimension].length - 1 >
        selectedDrillDownDimensionIndex.index
      ) {
        setStateForChart(
          graphStateData,
          selectedDimension,
          selectedDrillDownDimensionIndex.index + 1,
          { filterBy: value.payload }
        )
        setSelectedGraphPoint(value)
      }
    }
  }

  const { t } = useTranslation()

  let graphId = `graphWrap-${chartTitle}-${chartId || ''}${
    graphStyle && graphStyle.fullScreen ? '-full' : ''
  }`

  // NOTE: Used for setting Chart State on Component load.
  React.useEffect(() => {
    setChartDataOnComponentLoad()
    //FIXME: Hack used for Calling only this useEffect
    setIsFirstTime(false)
    // eslint-disable-next-line
  }, [globalFilter])

  //NOTE: call when user click on a chart for drillDown
  React.useEffect(() => {
    if (!isEmpty(selectedGraphPoint)) {
      setSelectedDrillDownDimensionIndex({
        isReload: true,
        index: selectedDrillDownDimensionIndex.index + 1
      })
    }
    // eslint-disable-next-line
  }, [selectedGraphPoint])

  //NOTE: set data for chart on drilldown dimension index change
  React.useEffect(() => {
    if (!isFirstTime) {
      setDataForChart(
        selectedDimension,
        selectedDrillDownDimensionIndex,
        graphStateData
      )
    }
    // eslint-disable-next-line
  }, [selectedDrillDownDimensionIndex])

  //NOTE: call when dimension changes or pivot changes
  React.useEffect(() => {
    if (!isFirstTime) {
      if (selectedDrillDownDimensionIndex.index === 0) {
        setDataForChart(
          selectedDimension,
          selectedDrillDownDimensionIndex,
          graphStateData
        )
      } else {
        setSelectedDrillDownDimensionIndex({ isReload: false, index: 0 })
      }
    }
    // eslint-disable-next-line
  }, [selectedDimension])

  //NOTE: First Time called when page load and initialize state and data to chart
  async function setChartDataOnComponentLoad() {
    const { getDataForChart } = dataConfig
    const stateObj = generateStateForChart(graphConfig)
    let { query, stacked } = stateObj[selectedDimension][
      selectedDrillDownDimensionIndex.index
    ]
    setLoading(true)
    if (
      query.benchmarkDimension?.value &&
      !isNaN(Number(query.benchmarkDimension.value))
    ) {
      setBenchMarkInput(Number(query.benchmarkDimension.value))
    }

    const apiData = await getDataForChart(query, globalFilter)
    const { data: graphData, yLabelKey } = generateDataForChart(
      apiData,
      stacked,
      query.benchmarkDimension,
      graphConfig.chartType
    )
    stateObj[selectedDimension][
      selectedDrillDownDimensionIndex.index
    ].data = graphData
    stateObj[selectedDimension][
      selectedDrillDownDimensionIndex.index
    ].displayData = graphData.slice(0, 10)
    stateObj[selectedDimension][
      selectedDrillDownDimensionIndex.index
    ].yLabelKey = yLabelKey
    let noOfDataPoint = graphData.length > 10 ? 10 : graphData.length
    stateObj[selectedDimension][
      selectedDrillDownDimensionIndex.index
    ].displayDataLength = noOfDataPoint
    setLoading(false)
    setGraphStateData(stateObj)
  }

  async function setDataForChart(
    selectedDimension: string,
    selectedDrillDownDimensionIndex: { isReload: boolean; index: number },
    stateObj: IKeyValue<IGraphStateData[]>
  ) {
    const { getDataForChart } = dataConfig
    const selectedDimensionData = stateObj[selectedDimension]
    let { query, stacked, filterBy, data } = cloneDeep(
      selectedDimensionData[selectedDrillDownDimensionIndex.index]
    )
    query.filter = query.filter ? query.filter : []
    for (let i = 0; i <= selectedDrillDownDimensionIndex.index; i++) {
      if (selectedDimensionData[i].filterBy) {
        const { query: prevQuery } = selectedDimensionData[i - 1]
        const filterObj: IFilterData = {
          filterValue: {
            value:
              (selectedDimensionData[i].filterBy as any).key ??
              (selectedDimensionData[i].filterBy as any).label
          },
          filterType: { value: 'eq' },
          filterKey: {
            value: prevQuery.dimension[graphDataMapping.dimension.value]
          },
          condition: { value: 'include' }
        }
        query.filter.push([filterObj])
      }
    }
    if (isEmpty(data) || selectedDrillDownDimensionIndex.isReload) {
      setLoading(true)

      // If this entry has _rawApiData (stored when its parent level was loaded),
      // resolve the data client-side from the clicked item's breakDownDimension.
      const currentEntry = selectedDimensionData[selectedDrillDownDimensionIndex.index]
      const currentRawData: any[] | undefined = (currentEntry as any)?._rawApiData
      const clickedLabel =
        (currentEntry?.filterBy as any)?.key ??
        (currentEntry?.filterBy as any)?.label

      let apiData: any[]
      if (currentRawData && clickedLabel) {
        const matched = currentRawData.find(
          (item: any) => item.label === clickedLabel
        )
        apiData = matched?.breakDownDimension ?? []
      } else {
        apiData = await getDataForChart(query, globalFilter)
      }

      const { data: graphData, yLabelKey } = generateDataForChart(
        apiData,
        stacked,
        query.benchmarkDimension,
        query.chartType
      )
      let noOfDataPoint = graphData.length > 10 ? 10 : graphData.length

      const displayData = graphData.slice(0, noOfDataPoint)
      const config = {
        data: graphData,
        yLabelKey: yLabelKey,
        displayData,
        displayDataLength: noOfDataPoint
      }

      // If the API response contains breakDownDimension on each item, this
      // level supports one more drill-down. Append a dynamic state entry so
      // the next click is enabled — client-side, no extra API call needed.
      const hasBreakDown =
        Array.isArray(apiData) &&
        apiData.some(
          (item: any) =>
            Array.isArray(item.breakDownDimension) &&
            item.breakDownDimension.length > 0
        )
      const nextIndex = selectedDrillDownDimensionIndex.index + 1
      const updatedStateObj = cloneDeep(stateObj)
      if (hasBreakDown && !updatedStateObj[selectedDimension][nextIndex]) {
        updatedStateObj[selectedDimension].push({
          query: cloneDeep(query),
          data: [],
          displayData: [],
          yLabelKey: [],
          xLabelKey: 'label',
          stacked: false,
          _rawApiData: apiData
        } as any)
      }

      setStateForChart(
        updatedStateObj,
        selectedDimension,
        selectedDrillDownDimensionIndex.index,
        config
      )
      // setNoOfData(noOfDataPoint)
    }
  }

  //NOTE: Set State for Chart on config basis
  function setStateForChart(
    stateObj: IKeyValue<IGraphStateData[]>,
    selectedDimension: string,
    selectedDrillDownDimensionIndex: number,
    config: Partial<TGraphStateConfig>
  ) {
    const tempData = cloneDeep(stateObj)
    Object.keys(config).forEach(ele => {
      let tempKey: TGraphStateDataKeys = ele as TGraphStateDataKeys
      ;(tempData[selectedDimension][selectedDrillDownDimensionIndex] as any)[
        tempKey
      ] = config[tempKey]
    })
    setGraphStateData(tempData)
    setLoading(false)
  }

  function generateQueryForChart(
    graphConfig: IChartConfig,
    dimension: IDimension,
    dimensionData: any,
    drillDownData?: Partial<IDrillDownDimension>
  ) {
    const {
      metricList,
      dataSource,
      hideSort,
      sortType,
      sortKey,
      filter,
      chartType
    } = graphConfig
    const query: Partial<IApiChartConfig> = {}
    query.dimension = dimensionData
    query.metric = metricList[0]
    query.metrics = metricList
    query.dataSource = dataSource
    query.hideDimension = dimension.hideDimension
    if (dimension.breakdownDimension) {
      query.breakdownDimension = dimension.breakdownDimension
    }
    if (dimension.benchmarkDimension) {
      query.benchmarkDimension = dimension.benchmarkDimension
    }
    if (dimension.dimensionalFilters) {
      query.dimensionalFilters = dimension.dimensionalFilters
    }
    if (dimension.dimensionalAggs) {
      query.dimensionalAggs = dimension.dimensionalAggs
    }
    if (dimension.layout || drillDownData?.layout) {
      query.layout = drillDownData?.layout
        ? drillDownData.layout
        : dimension.layout
    }
    query.metricUnit = metricList[0].metricUnit
    query.dimensionUnit = dimension.dimensionUnit
    query.sortKey = sortKey
    query.chartType = dimension.chartType || chartType
    query.sortType = sortType
    if (dimension.sortType || drillDownData?.sortType) {
      query.sortType = drillDownData?.sortType
        ? drillDownData.sortType
        : dimension.sortType
    }
    query.hideSort = hideSort
    if (dimension.hideSort || drillDownData?.hideSort) {
      query.hideSort = drillDownData?.hideSort
        ? drillDownData.hideSort
        : dimension.hideSort
    }
    query.filter = filter && filter.filterData
    query.dimensionDescription = dimension.dimensionDescription
    query.metricDescription = dimension.metricDescription

    const clonedDrillDownData = cloneDeep(drillDownData)
    if (clonedDrillDownData) {
      delete clonedDrillDownData.drillDownDimension
    }

    return { ...query, ...clonedDrillDownData }
  }

  function generateStateForChart(graphConfig: IChartConfig) {
    const { dimensions } = graphConfig
    const { graphDataMapping } = dataConfig
    const stateObj: IKeyValue<IGraphStateData[]> = {}
    dimensions.forEach(dimension => {
      stateObj[dimension.dimension[graphDataMapping.dimension.value]] = [
        {
          query: generateQueryForChart(
            graphConfig,
            dimension,
            dimension.dimension
          ),
          data: [],
          displayData: [],
          yLabelKey: [],
          xLabelKey: 'label',
          stacked:
            dimension.breakdownDimension || dimension.benchmarkDimension
              ? true
              : false
        }
      ]
      if (dimension.isDrillDown && dimension.drillDownDimensions) {
        dimension.drillDownDimensions.forEach(drillDownDimension => {
          stateObj[dimension.dimension[graphDataMapping.dimension.value]].push({
            query: generateQueryForChart(
              graphConfig,
              dimension,
              drillDownDimension.drillDownDimension,
              drillDownDimension
            ),
            displayData: [],
            data: [],
            yLabelKey: [],
            xLabelKey: 'label',
            stacked:
              drillDownDimension.breakdownDimension ||
              dimension.benchmarkDimension
                ? true
                : false
          })
        })
      }
    })
    return stateObj
  }

  //NOTE: Render Graph Header
  function renderGraphHeader() {
    return (
      <GraphHeader
        selectedDimension={selectedDimension}
        setSelectedDimension={setSelectedDimension}
        config={{ dataConfig, graphConfig }}
        selectedDrillDownDimensionIndex={selectedDrillDownDimensionIndex}
        setSelectedDrillDownDimensionIndex={setSelectedDrillDownDimensionIndex}
        setStateForChart={setStateForChart}
        graphStateData={graphStateData}
        globalFilter={globalFilter}
      />
    )
  }
  //NOTE: Render n level  Dimension
  function renderDimensionTraveller() {
    return (
      <DimensionTraveller
        selectedDimension={selectedDimension}
        selectedDrillDownDimensionIndex={selectedDrillDownDimensionIndex}
        setStateForChart={setStateForChart}
        graphStateData={graphStateData}
        setSelectedDrillDownDimensionIndex={setSelectedDrillDownDimensionIndex}
        setSelectedGraphPoint={setSelectedGraphPoint}
        setDataForChart={setDataForChart}
        graphDataMapping={graphDataMapping}
        graphStyle={graphStyle}
        dataConfig={dataConfig}
        graphConfig={graphConfig}
        globalFilter={globalFilter}
        benchmarkInput={benchmarkInput}
        setBenchMarkInput={setBenchMarkInput}
        showGraph={showGraph}
        setShowGraph={setShowGraph}
        FullScreenComponent={FullScreenComponent}
      />
    )
  }
  //NOTE:Render Graph
  function renderGraph() {
    try {
      const { displayData: data, yLabelKey, query, stacked } = graphStateData[
        selectedDimension
      ][selectedDrillDownDimensionIndex.index]
      const dataWithKeys = data.map((type: any) => ({
        ...type,
        key: type.label
      }))
      let tdata = dataWithKeys
      var translatedYLabelKeys = yLabelKey
      const translatedColors: any = graphConfig.colors || {}
      if (
        (query.chartType ? query.chartType : graphConfig.chartType) === 'pie' ||
        'bar'
      ) {
        tdata = dataWithKeys.map((type: any) => {
          const translatedLabel = t(toTileCase(type.label.replace(/_/g, ' ')), {
            defaultValue: toTileCase(type.label.replace(/_/g, ' '))
          })
          if (stacked) {
            translatedYLabelKeys = []
            yLabelKey.map((key: string) => {
              const translatedKey = t(toTileCase(key.replace(/_/g, ' ')))
              type[translatedKey] = type[key]
              if (translatedColors[key]) {
                translatedColors[translatedKey] = translatedColors[key]
              }
              translatedYLabelKeys.push(translatedKey)
              return translatedKey
            })
          }
          if (graphConfig?.colors?.[type.label.toLowerCase()]) {
            translatedColors[translatedLabel] =
              graphConfig?.colors?.[type.label.toLowerCase()]
          }
          return {
            ...type,
            label: translatedLabel
          }
        })
      }
      return (
        <Graph
          type={
            (query.chartType
              ? query.chartType
              : graphConfig.chartType) as TGraphType
          }
          data={tdata}
          showLegends={showLegends}
          XLabelKey={'label'}
          YLabelKey={translatedYLabelKeys}
          ratio={graphStyle ? graphStyle.graphScreenRatio : 16 / 5}
          events={events}
          stacked={stacked ? 'tower' : undefined}
          config={{
            colors: translatedColors || {},
            legendsList: [],
            xAxisUnit: query
              ? query.dimensionUnit[graphDataMapping['unit'].value]
              : '',
            yAxisUnit: query
              ? query.metricUnit[graphDataMapping['unit'].value]
              : ''
          }}
          labelColor="white"
          YLabel={t(
            (query.dimensionDescription
              ? query.dimensionDescription
              : graphConfig.dimensionDescription ?? ''
            ).trim()
          )}
          XLabel={t(
            (query.metricDescription
              ? query.metricDescription
              : graphConfig.metricDescription ?? ''
            ).trim()
          )}
          selector={graphId}
          layout={query.layout ? query.layout : graphConfig.layout}
        />
      )
    } catch (ex:any) {
      console.warn('Something went wrong', ex)
    }
  }

  function renderScoreCard() {
    const selectedGraph =
      graphStateData[selectedDimension] &&
      graphStateData[selectedDimension][selectedDrillDownDimensionIndex.index]
    return (
      <ScoreCard
        selectedGraph={selectedGraph}
        graphDataMapping={dataConfig.graphDataMapping}
        index={selectedDrillDownDimensionIndex.index}
        pageSize={pageSize}
      />
    )
  }

  const { width } = useWindowDimensions()

  //FIXME: Add Destructuring in case of nested objects.
  return (
    <GraphRendererContainer
      width={width}
      containerStyle={{
        ...(graphStyle ? graphStyle.wrapper : {})
        // order: isEmpty(graphData) ? 1 : 0
      }}
    >
      {renderGraphHeader()}
      <Card bg="white" variant="s" style={{ height: '100%' }}>
        <RenderControl
          data={
            graphStateData[selectedDimension] &&
            graphStateData[selectedDimension][
              selectedDrillDownDimensionIndex.index
            ]?.data
          }
          isSuccess={
            !loading &&
            !isEmpty(
              graphStateData[selectedDimension]?.[
                selectedDrillDownDimensionIndex.index
              ]?.data
            )
          }
          isLoading={loading}
          variant={'m'}
          message={
            !isEmpty(
              graphStateData[selectedDimension]?.[
                selectedDrillDownDimensionIndex.index
              ]?.data
            )
              ? ''
              : t('No Data Found')
          }
        >
          {graphStateData[selectedDimension]?.[
            selectedDrillDownDimensionIndex.index
          ] && (
            <div id={graphId}>
              {renderDimensionTraveller()}
              {showGraph ? renderGraph() : renderScoreCard()}
            </div>
          )}
        </RenderControl>
      </Card>
    </GraphRendererContainer>
  )
}
export default GraphRenderer
// export default React.memo(GraphRenderer, (prevProp, newProp) => {
//   return isEqual(prevProp, newProp)
// })
